<script>
import delay from 'lodash/delay';
import { mapState } from 'vuex';
import VsButton from 'viralstyle-ui/src/VsButton';
import VsInput from 'viralstyle-ui/src/VsInput';
import VsLogoLoader from 'viralstyle-ui/src/VsLogoLoader';

import config from 'config';
import { loginValidator } from 'domains/base/_base-validator';

export default {
  components: {
    VsButton,
    VsInput,
    VsLogoLoader,
  },
  data: () => ({
    username: '',
    password: '',
    loginFailed: false,
    validatonFailed: false,
    visible: false,
    loginValidator,
  }),
  computed: {
    ...mapState({
      ready: state => state.ready,
      processing: state => state.processing,
      loginVisible: state => state.loginVisible,
    }),
    forgotPasswordLink() {
      return `${config.APP_PROTOCOL}://${config.APP_URL}/forgot-password`;
    },
  },
  watch: {
    loginVisible(val) {
      if (val) {
        this.$_show();
      } else {
        this.$_hide();
      }
    },
  },
  created() {
    // 1) onload function for recaptcha
    window.grecaptchaLoaded = this.$_initGrecaptcha;
  },
  methods: {
    $_show() {
      this.visible = true;

      delay(() => {
        if (this.$refs.loginForm) this.$refs.loginForm.getElementsByClassName('vs-input')[0].focus();
      }, 200);
    },
    $_hide() {
      this.visible = false;
    },
    $_initGrecaptcha() {
      // 2) render recaptcha
      window.grecaptcha.render('google_recaptcha', {
        sitekey: config.GOOGLE_RECAPTCHA_SITEKEY,
        callback: token => this.recaptchaCallback(token),
        size: 'invisible',
      });
    },
    async submitLogin(e) {
      e.preventDefault();
      this.loginFailed = false;
      this.validatonFailed = false;

      try {
        let valid = await this.loginValidator.validateAll({ username: this.username, password: this.password });

        if (valid) {
          // 3) execute recaptcha
          if (config.GOOGLE_RECAPTCHA_SITEKEY) window.grecaptcha.execute();
          else await this.performLogin();
        } else {
          this.validatonFailed = true;
        }

      } catch (error) {
        this.loginFailed = true;
      }
    },
    async recaptchaCallback(token) {
      // 4) reset recaptcha
      window.grecaptcha.reset();

      try {
        await this.performLogin(token);
      } catch (e) {
        this.loginFailed = true;
      }
    },
    async performLogin(token) {
      await this.$store.dispatch('loginLegacy', { email: this.username, password: this.password, token });

      this.username = '';
      this.password = '';
    },
    updateData(e, property) {
      this[property] = e.target.value;
      this.loginValidator.validate(property, e.target.value);
    },
  },
};
</script>

<template lang='pug'>
transition(name='login')
  .logo.logo--login(v-if='visible || !ready')
    .loading-logo(v-if='!ready && !visible')
      vs-logo-loader(white, slow)
    .logo__svg(v-if='visible')
      svg(xmlns="http://www.w3.org/2000/svg", viewBox="0 0 94.349997 86.700001", version="1.1", width="94.349998", height="86.699997")
        path.logo__right(d="m 24.712404,40.37347 15.56928,14.688 c 0.4896,0.4896 0.78336,1.37088 1.07712,2.05632 1.9584,5.8752 4.01472,11.7504 5.58144,16.45056 4.896,-17.03808 10.08576,-35.05536 15.27552,-53.3664 5.3856,1.9584 10.57536,3.81888 16.05888,5.67936 1.9584,-4.896 3.81888,-9.596159 5.77728,-14.590077 l -14.1984,-7.7356762 23.79456,7.1481572 c -3.62304,8.323196 -7.14816,16.548476 -10.86912,24.871676 -6.3546,-1.7136 -6.3546,-1.7136 -11.45664,-3.13344 -5.975074,17.73964 -12.116483,36.48422 -17.136,51.8976 -0.4896,1.468797 -1.17504,1.860477 -2.64384,1.860477 -3.23136,-0.0979 -6.46272,0 -9.59616,-0.0979 -0.58752,0 -1.478606,-0.17392 -1.76256,-0.979197 C 34.016777,67.63374 28.398118,51.12113 24.712404,40.37347 Z")
        path.logo__left(d="m 39.664187,48.5879 c -5.216047,-4.76286 -10.8566,-9.88477 -15.386,-14.112 -0.98,-0.882 -1.764,-1.078 -2.94,-0.686 -3.234,0.98 -6.37,1.862 -9.898,2.744 C 7.9121875,28.3999 4.3841875,20.3639 0.66018746,12.131903 8.6437511,8.4521464 17.38625,4.6609067 24.474187,1.645907 c 1.47,-0.588 3.136,-0.88200012 4.704,-0.98000012 3.234,-0.196 6.468,0 9.8,0 0.784,4.99799972 2.548,9.40799712 8.428,9.79999712 4.116,0.294 7.35,-2.5479976 9.31,-7.6439971 1.078,5.1939995 -2.548,10.8779951 -7.644,12.7399941 -6.664,2.449999 -13.328,0.196 -16.17,-5.6839966 -2.94,-0.2939985 -5.684,0 -8.33,1.3719986 -3.92,1.959999 -8.134,3.625998 -12.25,5.487998 1.078,2.841999 2.156,5.585999 3.332,8.231999 0.196,0.392 1.274,0.784 1.764,0.686 4.214,-1.078 8.526,-2.254 12.74,-3.528 1.47,-0.392 2.058,-0.294 2.45,1.274 3.332,12.446 3.332,12.446 7.056,25.186 z")
    form.login#login_form(v-if='visible', @submit='submitLogin', ref='loginForm')
      vs-input(
        large,
        full-width,
        type='text',
        name='email',
        placeholder='Email',
        :value='username',
        :error='validatonFailed && loginValidator.hasError("username")',
        @input='updateData($event, "username")',
      )
      .vs-error(v-show='validatonFailed && loginValidator.hasError("username")') {{loginValidator.firstError('username')}}
      vs-input(
        large,
        full-width,
        type='password',
        name='password',
        placeholder='Password',
        :value='password',
        :error='validatonFailed && loginValidator.hasError("password")',
        @input='updateData($event, "password")',
      )
      .vs-error(v-show='validatonFailed && loginValidator.hasError("password")') {{loginValidator.firstError('password')}}
      vs-button(
        large,
        form='login_form',
        text='log in',
        :processing='processing',
        :disabled='processing',
      )
      .vs-error(v-show='loginFailed') Invalid credentials provided.
      h6.login__forgot
        strong
          a.vs-link(:href='forgotPasswordLink') Forgot Password?
</template>

<style>
@import '~import/index';

.logo--login {
  @extend %flex;
  @extend %flex--column;
  @extend %text-center;
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  overflow: auto;
  z-index: 2000;

  >.logo__svg {
    min-height: 30px;
    height: 55px;
    width: auto;

    >svg {
      @extend %full-height;
    }
  }
}

.loading-logo {
  height: 100px;
}

.login {
  margin: 2rem 0;
  width: 70%;
  max-width: 400px;

  >* {
    margin-top: 2rem;
    width: 100%;
  }

  .vs-input {
    background-color: transparent;
    border-radius: 0;
    border-width: 0 0 2px 0;
    border-style: solid;
    border-color: $primaryColor;
    color: $white;
    padding-left: 0;
    padding-right: 0;

    ~ .floatl__bg {
      background-color: transparent;
    }

    &:focus,
    &:hover {
      border-color: darken($primaryColor, 10%);

      & ~ .floatl__bg {
        background-color: transparent;
      }
    }

    &:-webkit-autofill {
      transition: none;
      -webkit-box-shadow: 0 0 0 50px $secondaryColor inset;
      -webkit-text-fill-color: $white;
    }

    &::placeholder {
      color: $white;
      padding-left: 0;
      padding-right: 0;
    }
  }

  .vs-button {
    &:focus {
      background-color: darken($primaryColor, 10%);
    }
  }

  .floatl__label {
    left: 0;
    color: $white;
    padding-left: 0;
    padding-right: 0;
  }

  &-enter-active,
  &-leave-active {
    transition: all 500ms;

    >.login,
    >.loading-logo {
      transition: all 500ms;
      transform: translate3d(0, 0, 0);

      >.logo-loader>.logo-loader__svg>.logo-loader__left,
      >.logo-loader>.logo-loader__svg>.logo-loader__right {
        animation: none;
        stroke-dashoffset: 0;
      }

      >.logo-loader>.logo-loader__svg>.logo-loader__left {
        fill: $primaryColor;
      }

      >.logo-loader>.logo-loader__svg>.logo-loader__right {
        fill: $white;
      }
    }
  }

  &-enter-active {
    transition-delay: 500ms;

    >.login {
      transition-delay: 500ms;
    }
  }

  &-enter,
  &-leave-to {
    height: 50px;
    width: 100%;

    @include mq(mf-md) {
      height: 100px;
      width: 130px;
    }

    >.login {
      margin: 0;
      height: 0;
    }

    >.loading-logo {
      height: 55px;
      @include mq(sm) {
        height: 30px;
      }
    }
  }

  &-leave,
  &-enter-to {
    height: 100%;
    width: 100%;

    >.login {
      margin: 2rem 0;
      height: 224px;
      @include mq(mf-xs) {
        height: 232px;
      }
      @include mq(mf-md) {
        height: 241px;
      }
    }

    >.loading-logo {
      height: 150px;
    }
  }

  &__forgot {
    @extend %letter-spacing-1;

    .vs-link {
      color: $white;

      &:hover,
      &:visited,
      &:focus,
      &:active {
        color: darken($white, 10%);
      }
    }
  }
}
</style>